/**
 * This page's traffic estimate beside the rival page's, on `/review` (owner, 2026-11-03,
 * TRAFFIC_ESTIMATE_CUTOVER.md D3 at page level; TECHNICAL_DEBT §0cu).
 *
 * DataForSEO changed how it estimates traffic on 1 November, so the estimate is not compared across
 * time any more. What still means something is the same formula across pages at one time: this
 * page against the page it is compared with on its comparison search (the compare target,
 * `compare-target.ts`). So the two are set side by side only when both estimates were made the same
 * way: each side carries the model its fetch time puts it under (`DATAFORSEO_ETV_MODEL_CUTOVER`,
 * owned by the backend's `dataforseo.ts`, which tags the sides before they get here).
 *
 * Not where Search Console is on (owner, 2026-11-03, TRAFFIC_ESTIMATE_CUTOVER §8 D5): see
 * `estimateBesideRival`. The card used to put this page's real clicks beside its own estimate there;
 * that variant is gone with it.
 *
 * The wording must not read like the header's click line ("Search Console clicks, 14 days before
 * crawl: 35 → 40"; memory: confusable states need different copy): this card says "estimated visits
 * a month" and never shows a change.
 *
 * One rule, read by the belt card, the Dashboard's page row and the everything export.
 */

/** One side's estimate, as the backend tags it. */
export interface PageEstimateSide {
  /** DataForSEO's estimated visits a month (ETV), unrounded. */
  visits: number;
  /** Which formula made it: `dataforseo_etv_2023` or `dataforseo_etv_2026` (`traffic-estimate-model.ts`). */
  model: string;
  /** ISO day it was fetched. */
  fetchedOn: string;
}

/** What the page's loads hand the rule (`page-estimate-pair-load.ts`). Null when either side has no estimate. */
export interface PageEstimatePairSource {
  /** The comparison search the rival was picked on. */
  query: string | null;
  yours: PageEstimateSide;
  rival: PageEstimateSide & {
    url: string;
    domain: string;
    /** The rival's position on the comparison search; null for a page not ranked there. */
    position: number | null;
  };
  /** Search Console is on for this workspace and domain (`switchedOnConnection`). */
  searchConsoleOn: boolean;
}

export interface EstimateBesideRivalView {
  /** The card's heading. */
  title: string;
  yours: {
    label: string;
    estimate: string;
  };
  rival: {
    /** The rival page, as a host and path. */
    label: string;
    url: string;
    /** "#1 for “client portal software”", or null when neither is known. */
    context: string | null;
    estimate: string;
  };
  /** Said once under the two lines. */
  note: string;
  /** The whole card in one sentence: the Dashboard row and the export say this. */
  sentence: string;
}

export const ESTIMATE_BESIDE_RIVAL_TITLE = 'Estimated traffic, this page and the rival';

/** Whole visits from 10 up, one decimal below, as the export rounds them. */
function visitsNumber(n: number): string {
  return n >= 10 ? Math.round(n).toLocaleString('en-US') : String(Math.round(n * 10) / 10);
}

function estimatePhrase(n: number): string {
  if (n <= 0) return 'no estimated visits';
  const v = visitsNumber(n);
  return `about ${v} estimated ${v === '1' ? 'visit' : 'visits'} a month`;
}

function hostAndPath(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    return `${u.hostname.replace(/^www\./, '')}${path}`;
  } catch {
    return url;
  }
}

function quoted(text: string): string {
  return `“${text.replace(/\s+/g, ' ').trim()}”`;
}

function validSide(side: PageEstimateSide | null | undefined): side is PageEstimateSide {
  return !!side && Number.isFinite(side.visits) && side.visits >= 0 && !!side.model;
}

/**
 * The card, or null when there is nothing to set side by side:
 * - Search Console is on (owner, 2026-11-03, TRAFFIC_ESTIMATE_CUTOVER §8 D5). DataForSEO's new
 *   estimate is too low for the rival pages it is set against: on ManyRequests (2026-11-03, 23 pages
 *   with the card) the rival showed about 1 estimated visit a month or none on 13, and this page's
 *   real clicks beat the rival's estimate on 11 (40 clicks against "0.7 visits"), so the card read as
 *   "your rival gets no traffic". Where Search Console is off, both sides are the same model's guess
 *   and no real number on the page contradicts it;
 * - either side has no estimate;
 * - the two estimates were not made the same way (one before DataForSEO's change, one after);
 * - both are zero: two "no estimated visits" lines compare nothing.
 */
export function estimateBesideRival(source: PageEstimatePairSource | null | undefined): EstimateBesideRivalView | null {
  if (!source || source.searchConsoleOn) return null;
  if (!validSide(source.yours) || !validSide(source.rival)) return null;
  if (source.yours.model !== source.rival.model) return null;
  if (source.yours.visits <= 0 && source.rival.visits <= 0) return null;

  const query = (source.query || '').trim();
  const context =
    source.rival.position != null && query
      ? `#${source.rival.position} for ${quoted(query)}`
      : query
        ? `compared on ${quoted(query)}`
        : null;

  const note =
    "Both estimates are DataForSEO's, made the same way for both pages, so they compare with each other. Neither is a count of real visits.";

  const yoursEstimate = estimatePhrase(source.yours.visits);
  const rivalEstimate = estimatePhrase(source.rival.visits);
  const rivalLabel = hostAndPath(source.rival.url);
  const sentence =
    `Estimated by DataForSEO, the same way for both pages: this page ${yoursEstimate}` +
    `; ${rivalLabel}${context ? `, ${context},` : ''} ${rivalEstimate}.`;

  return {
    title: ESTIMATE_BESIDE_RIVAL_TITLE,
    yours: { label: 'This page', estimate: yoursEstimate },
    rival: { label: rivalLabel, url: source.rival.url, context, estimate: rivalEstimate },
    note,
    sentence
  };
}
